import { ApiError } from "../../shared/api/client";
import { isTransientProductionFailure } from "./operator-connection";
import { useEffect, useState } from "react";
import { Tablet } from "lucide-react";
import type { SessionUser } from "../../shared/auth/access";
import { SystemState } from "../../shared/components/SystemState";
import { platformBrand } from "../../shared/branding";
import { ProductionExecutionModule } from "./ProductionExecutionModule";
import { OperatorPinLogin } from "./terminal/OperatorPinLogin";
import type { TerminalBootstrap } from "./models";
import { productionGateway } from "./production-gateway";
import "./tablet-terminal-page.css";

const codeKey = "anl-production-terminal-code";
const autoKey = "anl-production-terminal-autostart";
const fragment = new URLSearchParams(window.location.hash.slice(1));
const setupCode = fragment.get("terminal");
const enrollmentCode = fragment.get("enrollment");
if (setupCode && enrollmentCode) window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
let enrollment: Promise<unknown> | null = null;

export function shouldOpenTabletTerminal() {
  return window.location.pathname === "/operator" || (window.location.pathname === "/" && window.localStorage.getItem(autoKey) === "true");
}

export function TabletTerminalPage() {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loginContext, setLoginContext] = useState<TerminalBootstrap | null>(null);
  const [message, setMessage] = useState("Tablet bağlantısı doğrulanıyor…");
  const [retry, setRetry] = useState(0);
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    let disposed = false;
    let loading = false;
    const load = async () => {
      if (loading) return;
      loading = true;
      try {
        const code = setupCode || window.localStorage.getItem(codeKey);
        if (!code) throw new Error("Sistem yöneticisinin oluşturduğu tablet kurulum bağlantısını bu tablette açın.");
        if (setupCode && enrollmentCode) {
          enrollment ??= productionGateway.enrollTerminal(code, enrollmentCode);
          try { await enrollment; } catch { enrollment = null; }
        }
        const context = await productionGateway.bootstrapTerminal(code);
        if (setupCode && enrollmentCode) enrollment = Promise.resolve();
        if (!context.terminal.autoLogin) throw new Error("Bu tabletin doğrudan operatör erişimi sistem yöneticisi tarafından açılmalı.");
        window.localStorage.setItem(codeKey, code);
        window.localStorage.setItem(autoKey, "true");
        if (!context.session || context.session.status === "CLOSED") {
          if (!disposed) {
            setUser(null);
            setLoginContext(context.assignments.length ? context : null);
            setMessage("Bu hat ve vardiyaya sorumlu operatör ataması bekleniyor.");
          }
          return;
        }
        const session = context.session;
        if (!disposed) { setLoginContext(null); setUser({ id: session.operatorId, name: session.operatorName, email: "", initials: session.operatorName.slice(0, 2), title: "Operatör", departmentId: null, department: "Üretim", assignedRoles: ["URETIM_OPERATORU"], roles: ["URETIM_OPERATORU"], role: "URETIM_OPERATORU", roleLabel: "Operatör", permissions: ["production.entry.read", "production.entry.write", "production.run.execute"], dataScope: "assigned" }); }
      } catch (error) { if (!disposed) { if (!(error instanceof ApiError && isTransientProductionFailure(error.code))) { setUser(null); setLoginContext(null); } setMessage(error instanceof Error ? error.message : "Tablet doğrulanamadı."); } }
      finally { loading = false; if (!disposed) setChecking(false); }
    };
    void load();
    const interval = window.setInterval(() => void load(), 15_000);
    return () => { disposed = true; window.clearInterval(interval); };
  }, [retry]);
  if (!user && loginContext) return <OperatorPinLogin
    key={`${loginContext.terminal.id}:${loginContext.shift.shiftDate}:${loginContext.shift.shiftCode}:${loginContext.assignments.map((item) => item.operatorId).join(",")}`}
    context={loginContext}
    onAuthenticated={async () => { setLoginContext(null); setChecking(true); setRetry((value) => value + 1); }}
  />;
  return user ? <ProductionExecutionModule user={user} notify={setMessage} dataRefreshVersion={retry} onLogout={async () => {
    const code = window.localStorage.getItem(codeKey);
    if (code) await productionGateway.lockTerminal(code);
    setRetry((value) => value + 1);
  }} /> : <div className="tablet-terminal-page"><SystemState
    mode="page"
    brand={{ name: platformBrand.name, description: "Operatör terminali" }}
    title={checking ? "Tablet doğrulanıyor" : "Tablet bağlantısı bekleniyor"}
    description={checking ? "Tablet ve hat bağlantısı kontrol ediliyor…" : message}
    tone={checking ? "loading" : "neutral"}
    icon={checking ? undefined : <Tablet aria-hidden="true" />}
    primaryAction={{ label: checking ? "Kontrol ediliyor…" : "Bağlantıyı kontrol et", disabled: checking,
      onClick: () => { setChecking(true); setRetry((value) => value + 1); } }}
    reference={setupCode || window.localStorage.getItem(codeKey) || undefined}
    referenceLabel="Tablet kimliği"
    assistance="Bağlantı 15 saniyede bir kontrol edilir. Cihaz taslakları ve bekleyen kayıtlar silinmez; ancak kimlik doğrulanmadan başka operatöre gösterilmez veya gönderilmez. Çevrimdışı yeniden açılışta kayıt ekranı için sunucu doğrulaması gerekir."
  /></div>;
}
